import * as React from "react"
import { Navigate, useLocation } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuth } from './AuthContext'
import type { Role } from './types'
import { homePathForRole } from './types'
import { PageLoader } from '@/components/feedback/PageLoader'

interface Props {
  children: ReactNode
  roles?: Role[]
}

/**
 * Route guard. Renders the page-level loader while the session check is in
 * flight to avoid a flash of unauthenticated UI. Redirects to /login on
 * failure, preserving the attempted location so we can return there after
 * sign-in.
 */
export function RequireAuth({ children, roles }: Props): React.JSX.Element {
  const { user, loading, hasRole } = useAuth()
  const location = useLocation()

  if (loading) return <PageLoader />

  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />
  }

  if (roles && roles.length > 0 && !hasRole(...roles)) {
    // User is logged in but doesn't have the required role for this route.
    // Redirect to their actual home instead of a dead-end /forbidden page.
    return <Navigate to={homePathForRole(user.role)} replace />
  }

  return <>{children}</>
}
